// src/pages/proyectos/components/MiembroFormModal.tsx
import { useState } from 'react';
import { Dialog, DialogContent, Button, Label } from '@/components/ui';
import { useAgregarMiembro } from '@/hooks/queries/useProyectos';
import { useUsuarios } from '@/hooks/queries/useUsuarios';
import { ROL_PROYECTO_LABELS } from '@/types';
import type { RolProyecto } from '@/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';

interface Props {
  open: boolean;
  onClose: () => void;
  proyectoId: string;
  miembrosActuales: string[];
}

export function MiembroFormModal({ open, onClose, proyectoId, miembrosActuales }: Props) {
  const { data: usuarios } = useUsuarios();
  const agregarMutation = useAgregarMiembro();
  const [usuarioId, setUsuarioId] = useState('');
  const [rol, setRol] = useState<RolProyecto>('COLABORADOR');

  // Filtrar usuarios que ya son miembros
  const usuariosDisponibles = usuarios?.filter(
    (u) => !miembrosActuales.includes(u.id)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioId) return;

    try {
      await agregarMutation.mutateAsync({
        proyectoId,
        data: {
          usuario_id: usuarioId,
          rol,
        },
      });
      setUsuarioId('');
      setRol('COLABORADOR');
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <h2 className="text-xl font-semibold mb-6">Agregar Miembro</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Usuario *</Label>
            <Select
              value={usuarioId}
              onValueChange={setUsuarioId}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar usuario" />
              </SelectTrigger>
              <SelectContent>
                {usuariosDisponibles?.map((usuario) => (
                  <SelectItem key={usuario.id} value={usuario.id}>
                    {usuario.nombre}
                    {usuario.email && (
                      <span className="text-gray-400 ml-2">({usuario.email})</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {usuariosDisponibles?.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                Todos los usuarios ya son miembros del proyecto.
              </p>
            )}
          </div>

          <div>
            <Label>Rol</Label>
            <Select
              value={rol}
              onValueChange={(value) => setRol(value as RolProyecto)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROL_PROYECTO_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!usuarioId || agregarMutation.isPending}
            >
              {agregarMutation.isPending ? 'Agregando...' : 'Agregar miembro'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
